import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const errMsg = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fallback;
};

const PermissionPicker = ({ features, selected, onChange }) => {
  const groups = features.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});
  const toggle = (key, on) => onChange(on ? [...selected, key] : selected.filter((k) => k !== key));
  const toggleGroup = (items, on) => {
    const keys = items.map((f) => f.key);
    onChange(on ? [...new Set([...selected, ...keys])] : selected.filter((k) => !keys.includes(k)));
  };
  return (
    <div className="space-y-4 max-h-[42vh] overflow-y-auto pr-2" data-testid="permission-picker">
      {Object.entries(groups).map(([group, items]) => {
        const allOn = items.every((f) => selected.includes(f.key));
        return (
          <div key={group}>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{group}</p>
              <button type="button" onClick={() => toggleGroup(items, !allOn)} data-testid={`perm-group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-[11px] text-blue-600 hover:underline">{allOn ? "Clear all" : "Select all"}</button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {items.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selected.includes(f.key)} onCheckedChange={(on) => toggle(f.key, !!on)} data-testid={`perm-${f.key}`} />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const UserDialog = ({ open, onClose, features, editUser, onSaved }) => {
  const isEdit = !!editUser;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editUser?.name || "");
    setEmail(editUser?.email || "");
    setPassword("");
    setPerms(editUser?.permissions || []);
  }, [open, editUser]);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name, permissions: perms };
        if (password) body.password = password;
        await api.patch(`/users/${editUser.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", { name, email, password, permissions: perms });
        toast.success("User created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${editUser.name || editUser.email}` : "Create user"}</DialogTitle>
          <DialogDescription>Set login details and tick the features this user should see.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="user-name-input" />
            <Input placeholder="Email" type="email" value={email} disabled={isEdit}
              onChange={(e) => setEmail(e.target.value)} data-testid="user-email-input" />
          </div>
          <Input placeholder={isEdit ? "New password (leave blank to keep)" : "Password (min 6 chars)"} type="password"
            value={password} onChange={(e) => setPassword(e.target.value)} data-testid="user-password-input" />
          <div>
            <p className="text-sm font-semibold mb-2">Feature access</p>
            <PermissionPicker features={features} selected={perms} onChange={setPerms} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="user-dialog-cancel">Cancel</Button>
          <Button className="rounded-full" onClick={save} data-testid="user-dialog-save"
            disabled={saving || !name || (!isEdit && (!email || password.length < 6))}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function UsersPage() {
  const [users, setUsers] = useState(null);
  const [features, setFeatures] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data.users));
  useEffect(() => {
    load();
    api.get("/users/features").then(({ data }) => setFeatures(data.features));
  }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.email} deactivated` : `${u.email} activated`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  const featureLabel = (k) => features.find((f) => f.key === k)?.label || k;

  if (!users) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Create team members and control exactly which features each one can see and use. Admins have full access.
        </p>
        <Button className="rounded-full" onClick={() => { setEditUser(null); setDialogOpen(true); }} data-testid="add-user-button">
          <UserPlus className="h-4 w-4 mr-1.5" aria-hidden="true" /> Add user
        </Button>
      </div>
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {["User", "Role", "Access", "Status", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3">
                  <p className="font-medium">{u.name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin"
                    ? <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-0"><ShieldCheck className="h-3 w-3 mr-1" aria-hidden="true" />Admin</Badge>
                    : <Badge variant="secondary">Member</Badge>}
                </td>
                <td className="px-4 py-3 max-w-md">
                  {u.role === "admin" ? (
                    <span className="text-xs text-muted-foreground">All features</span>
                  ) : u.permissions.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No access</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.permissions.slice(0, 5).map((p) => (
                        <Badge key={p} variant="outline" className="text-[10px] font-normal">{featureLabel(p)}</Badge>
                      ))}
                      {u.permissions.length > 5 && (
                        <Badge variant="outline" className="text-[10px] font-normal">+{u.permissions.length - 5} more</Badge>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-0">Active</Badge>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.email}`} />
                      <span className="text-xs text-muted-foreground">{u.active ? "Active" : "Disabled"}</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role !== "admin" && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`user-edit-${u.email}`}
                        onClick={() => { setEditUser(u); setDialogOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" data-testid={`user-delete-${u.email}`}
                        onClick={() => remove(u)}>
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog open={dialogOpen} onClose={() => setDialogOpen(false)} features={features} editUser={editUser} onSaved={load} />
    </div>
  );
}
